import Script from 'next/script'
import { type FC, useEffect, useMemo, useRef, useState } from 'react'

import { useScriptOptOut } from '@/hooks/useScriptOptout'
import { RemoteParamKeys } from '@/lib/firebase/config'
import { useToggle } from '@/lib/firebase/hooks/useToggle'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { trackEvent } from '@/lib/ga/utils/tracking'

type FlippRegisterSlotOptions = {
  slotFillResultCallback?: (filled: boolean) => void
}

type FlippNativeXConfig = {
  siteId: number
  publisher: string
  zoneId: number
}

declare global {
  interface Window {
    flippxp?: {
      registerSlot?: (
        selector: string,
        publisher: string,
        siteId: number,
        zoneIds: number[],
        options?: FlippRegisterSlotOptions
      ) => void
      run: Array<() => void>
    }
  }
}

const SRP_ANCHOR_ID = 'flipp-ux-slot-srp-main'

const isPositiveNumber = (value: string | undefined) => {
  if (!value) return false
  const parsed = Number(value)
  return Number.isInteger(parsed) && parsed > 0
}

const getFlippNativeXConfig = (): FlippNativeXConfig | null => {
  if (
    !isPositiveNumber(process.env.NEXT_PUBLIC_FLIPP_NATIVEX_SITE_ID) ||
    !isPositiveNumber(process.env.NEXT_PUBLIC_FLIPP_NATIVEX_SRP_ZONE_ID) ||
    !process.env.NEXT_PUBLIC_FLIPP_NATIVEX_PUBLISHER
  ) {
    return null
  }

  return {
    siteId: Number(process.env.NEXT_PUBLIC_FLIPP_NATIVEX_SITE_ID),
    publisher: process.env.NEXT_PUBLIC_FLIPP_NATIVEX_PUBLISHER,
    zoneId: Number(process.env.NEXT_PUBLIC_FLIPP_NATIVEX_SRP_ZONE_ID),
  }
}

export const FlippNativeXSrp: FC = () => {
  const { shouldRenderScript } = useScriptOptOut()
  const [filled, setFilled] = useState<boolean | null>(null)
  const hasRegisteredRef = useRef(false)

  const config = useMemo(() => getFlippNativeXConfig(), [])

  const flippToggle = useToggle(RemoteParamKeys.FLIPP_NATIVEX_TOGGLE)
  const isFlippEnabled = flippToggle?.enabled ?? false
  const canRenderScript = shouldRenderScript('flippNativeX')

  useEffect(() => {
    if (!config || !canRenderScript || !isFlippEnabled || hasRegisteredRef.current) return
    hasRegisteredRef.current = true

    window.flippxp = window.flippxp || { run: [] }
    window.flippxp.run.push(() => {
      const options: FlippRegisterSlotOptions = {
        slotFillResultCallback: (isFilled) => {
          setFilled(isFilled)
          if (isFilled) {
            trackEvent({ action: GA_EVENT.FlippFlyerLoad })
          }
        },
      }

      window.flippxp?.registerSlot?.(
        `#${SRP_ANCHOR_ID}`,
        config.publisher,
        config.siteId,
        [config.zoneId],
        options
      )
    })
  }, [canRenderScript, config, isFlippEnabled])

  if (!config || !canRenderScript || !isFlippEnabled || filled === false) {
    return null
  }

  return (
    <>
      <Script
        id="flipp-nativex-controller-srp"
        data-testid="flipp-nativex-controller-srp"
        strategy="afterInteractive"
        src={`https://cdn-gateflipp.flippback.com/tag/js/flipptag.js?site_id=${config.siteId}`}
      />

      <div id={SRP_ANCHOR_ID} data-testid="flipp-nativex-anchor-srp">
        <div className="flipp-ux-header" style={{ display: 'none' }}></div>
      </div>
    </>
  )
}
